// A knowledge citation ("file-id#section") in readable words, for the
// Rujukan of an answer and of a document. The title comes from the Library
// when it is known; otherwise the id in words, without the Drive suffix a
// synced file carries ("reg-001-perpres-16-2018-1903mm0v"). A section id
// that is not made of words (a slug of a garbled PDF line such as
// "1-oipalukan-pdcc1j8") is left out instead of shown raw.

export interface CitationLabel {
  title: string;
  /** "Pasal 18", "Bagian: Harga perkiraan sendiri", or null. */
  where: string | null;
  /** The Drive file to open, when this entry came from one; null otherwise. */
  driveId: string | null;
}

/** "pasal-18" to "Pasal 18", "harga_perkiraan_sendiri" to "Bagian: Harga perkiraan sendiri"; null when it is not words. */
export function sectionLabel(section: string | null | undefined): string | null {
  const words = (section ?? "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  if (!/[\p{L}\p{N}]/u.test(words)) return null;
  const pasal = /^pasal\s+(.+)$/i.exec(words);
  if (pasal) return `Pasal ${pasal[1]}`;
  if (!readable(words)) return null;
  return `Bagian: ${words.charAt(0).toUpperCase()}${words.slice(1).toLowerCase()}`;
}

/**
 * True when most tokens read as words or plain numbers: no token mixes
 * letters and digits ("pdcc1j8"), and letter tokens of five or more carry a
 * vowel. "bagian 2", "harga perkiraan sendiri" pass; "1 oipalukan pdcc1j8" fails.
 */
function readable(words: string): boolean {
  const tokens = words.toLowerCase().split(" ").filter(Boolean);
  if (tokens.length === 0) return false;
  const bad = tokens.filter((t) => (/\d/.test(t) && /\p{L}/u.test(t) && !/^\d+[a-z]?$/.test(t)) || (/^\p{L}{5,}$/u.test(t) && !/[aiueo]/.test(t)));
  return bad.length === 0;
}

/** A knowledge id with no known title, in words: "perpres-16-2018" to "Perpres 16 2018" (a Drive suffix dropped). */
export function fileLabel(id: string): string {
  const base = id.replace(/-[a-z0-9]{8}$/i, (suffix) => (/\d/.test(suffix) && /[a-z]/i.test(suffix) ? "" : suffix));
  const words = base.replace(/[-_]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

export function citationLabel(code: string, files: { id: string; title: string }[] | null | undefined): CitationLabel {
  const hash = code.indexOf("#");
  const file = hash >= 0 ? code.slice(0, hash) : code;
  const section = hash >= 0 ? code.slice(hash + 1) : null;
  const entry = files?.find((f) => f.id === file);
  const known = entry?.title;
  // Only a Drive-generated entry can be opened; a hand-written knowledge file
  // has no original document behind it, so it stays plain text.
  const driveId = (entry as { driveId?: string | null } | undefined)?.driveId ?? null;
  if (known) return { title: known, where: sectionLabel(section), driveId };
  // An id that is not words either (a garbled file name) is named generically, never shown raw.
  const label = fileLabel(file);
  return { title: readable(label) ? label : "Dokumen basis pengetahuan", where: sectionLabel(section), driveId };
}
